import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Package, Search, X, ChevronDown, ChevronUp, Sparkles, Plus, Apple, Beef, Wheat, Milk, Egg, Carrot } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { pantryAPI } from '../utils/api';
import { useLatestRequest } from '../utils/requestLifecycle';
import { useScreenPerf } from '../utils/perf';
import LoadErrorCard from '../components/LoadErrorCard';
import {
  PANTRY_SECTIONS,
  hasItemNamed,
  ownedPantryIdentity,
  isAlreadyInPantry,
  pantryAddLockKeys,
  pantrySearchView,
} from '../utils/pantrySearch';

// Pantry categories with icons - ORDER MATCHES GROCERY LIST
// A GET /pantry or POST /pantry/items response as the five sections.
function toSections(data) {
  var out = { produce: [], proteins: [], dairy: [], pantry: [], other: [] };
  if (data && Array.isArray(data.items)) {
    data.items.forEach(function(item) {
      var cat = item.category || 'other';
      (out[cat] || out.other).push(item);
    });
    return out;
  }
  PANTRY_SECTIONS.forEach(function(section) { out[section] = (data && data[section]) || []; });
  return out;
}

const CATEGORIES = [
  { id: 'produce', name: 'Produce', icon: Carrot, color: 'text-green-500' },
  { id: 'proteins', name: 'Proteins', icon: Beef, color: 'text-red-500' },
  { id: 'dairy', name: 'Dairy & Eggs', icon: Milk, color: 'text-blue-400' },
  { id: 'pantry', name: 'Pantry', icon: Wheat, color: 'text-amber-500' },
  { id: 'other', name: 'Other', icon: Apple, color: 'text-purple-500' },
];

function Pantry() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // [PERF] temporary screen timing (MOUNT -> READY).
  useScreenPerf('PANTRY', !loading, loadError);
  const pantryLoad = useLatestRequest();
  
  // Pantry items by category - ORDER MATCHES GROCERY LIST
  const [pantryItems, setPantryItems] = useState({
    produce: [],
    proteins: [],
    dairy: [],
    pantry: [],
    other: []
  });
  // The latest Pantry, readable synchronously (duplicate checks and queued writes)
  const pantryRef = useRef(pantryItems);
  const applyPantry = useCallback((next) => {
    pantryRef.current = next;
    setPantryItems(next);
  }, []);
  // Every Pantry write runs in order, so a remove can't overwrite an add
  // that is still in flight (each write reads the Pantry when it runs).
  const writeChainRef = useRef(Promise.resolve());
  const enqueueWrite = useCallback((op) => {
    const run = writeChainRef.current.then(op, op);
    writeChainRef.current = run.catch(() => {});
    return run;
  }, []);
  
  // Expanded state for each category
  const [expandedCategory, setExpandedCategory] = useState('produce');
  
  // One global search. Suggestions help pick a Core Food, but never gate
  // saving: the typed row is available at once and the server resolves it.
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState('idle'); // idle | loading | done | error
  // Adds in flight (double-tap guard, keyed by name and Core ID)
  const addingRef = useRef(new Set());
  const [pendingAdd, setPendingAdd] = useState(null);

  // Fetch pantry data on mount
  useEffect(() => {
    fetchPantry();
  }, []);

  const fetchPantry = useCallback(() => {
    const req = pantryLoad.begin();
    setLoading(true);
    setLoadError(null);
    pantryAPI.getPantry({ signal: req.signal })
      .then(res => {
        if (!req.isCurrent()) return;
        applyPantry(toSections(res.data || {}));
      })
      .catch(err => {
        if (!req.isCurrent()) return;
        console.error('Failed to fetch pantry:', err);
        // Never fall through to an EMPTY editable Pantry (a later save could
        // overwrite the real one): show a recoverable error instead.
        setLoadError(err);
      })
      .finally(() => { if (req.isCurrent()) setLoading(false); });
  }, [applyPantry, pantryLoad]);

  // Debounced global Pantry search (Core Foods only; never USDA)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchStatus('idle');
      return undefined;
    }

    setSearchStatus('loading');
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      pantryAPI.search(q, 25)
        .then(res => {
          if (cancelled) return;
          setSearchResults((res.data && res.data.results) || []);
          setSearchStatus('done');
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Search failed:', err);
          setSearchResults([]);
          setSearchStatus('error');
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [query]);

  // Add ONE new item ({name} typed, or {food_id, name} from a suggestion).
  // The server decides its identity and returns the saved Pantry.
  const addItem = (payload) => {
    const keys = pantryAddLockKeys(payload);
    if (keys.some(key => addingRef.current.has(key))) return; // double tap
    const owned = payload.food_id
      ? isAlreadyInPantry({ food_id: payload.food_id }, ownedPantryIdentity(pantryRef.current))
      : hasItemNamed(pantryRef.current, payload.name);
    if (owned) {
      toast.message('Already in your Pantry');
      return;
    }
    keys.forEach(key => addingRef.current.add(key));
    setPendingAdd(payload.name);

    // Clear search immediately
    setQuery('');
    setSearchResults([]);
    setSearchStatus('idle');

    enqueueWrite(() => pantryAPI.addItems([payload], 'pantry')
      .then(res => {
        const data = res.data || {};
        applyPantry(toSections(data.pantry || {}));
        const result = (data.results || [])[0] || {};
        if (result.status === 'already') {
          toast.message('Already in your Pantry');
          return;
        }
        const item = result.item || {};
        const section = CATEGORIES.find(c => c.id === item.category);
        if (section) setExpandedCategory(section.id);
        toast.success(`Added ${item.name || payload.name} to ${section ? section.name : 'Other'}`);
      })
      .catch(err => {
        console.error('Failed to add pantry item:', err);
        toast.error('Failed to add item');
      })
      .finally(() => {
        keys.forEach(key => addingRef.current.delete(key));
        setPendingAdd(current => (current === payload.name ? null : current));
      }));
  };

  // A tapped suggestion keeps its Core Food and the name the user selected
  const addToPantry = (result) => {
    addItem({ food_id: result.food_id, name: result.display_name });
  };

  // The typed row: the server resolves the text to a Core Food when it safely can
  const addTypedToPantry = () => {
    const name = query.trim();
    if (!name) return;
    addItem({ name });
  };

  // Remove item from pantry
  const removeFromPantry = (category, item) => {
    const itemId = item.food_id || item.name;
    const without = (pantry) => ({
      ...pantry,
      [category]: (pantry[category] || []).filter(i => (i.food_id || i.name) !== itemId)
    });

    applyPantry(without(pantryRef.current)); // optimistic
    enqueueWrite(() => {
      const updatedPantry = without(pantryRef.current);
      applyPantry(updatedPantry);
      return pantryAPI.updatePantry(updatedPantry).catch(err => {
        console.error('Failed to update pantry:', err);
        toast.error('Failed to remove item');
        fetchPantry(); // show what the server actually holds
      });
    });

    toast.success(`Removed ${item.name}`);
  };

  // Clear all items
  const clearPantry = () => {
    const emptyPantry = {
      proteins: [],
      pantry: [],
      dairy: [],
      produce: [],
      other: []
    };
    applyPantry(emptyPantry);
    enqueueWrite(() => pantryAPI.updatePantry(emptyPantry).catch(err => {
      console.error('Failed to clear pantry:', err);
      fetchPantry(); // Refetch on error
    }));
    toast.success('Pantry cleared');
  };

  // Get total item count
  const getTotalItems = () => {
    return Object.values(pantryItems).reduce((sum, items) => sum + items.length, 0);
  };

  // Find meals with pantry items
  const findMealsWithPantry = () => {
    const allItems = Object.values(pantryItems).flat().map(item => item.name);
    if (allItems.length === 0) {
      toast.error('Add some items to your pantry first');
      return;
    }
    navigate('/plan?pantry=true');
  };

  // Render category section
  const renderCategory = (category) => {
    const { id, name, icon: Icon, color } = category;
    const isExpanded = expandedCategory === id;
    const items = pantryItems[id] || [];

    return (
      <motion.div
        key={id}
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border/40 rounded-xl overflow-hidden"
      >
        {/* Category Header */}
        <button
          onClick={() => setExpandedCategory(isExpanded ? '' : id)}
          className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
          data-testid={`category-${id}`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center`}>
              <Icon size={20} className={color} />
            </div>
            <div className="text-left">
              <p className="font-medium">{name}</p>
              <p className="text-xs text-muted-foreground">{items.length} items</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-0 space-y-3">
                {/* Selected Items as Pills */}
                {items.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {items.map((item, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 text-accent rounded-full text-sm"
                      >
                        {item.name}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFromPantry(id, item); }}
                          className="hover:bg-accent/20 rounded-full p-0.5 transition-colors"
                          data-testid={`remove-${item.name?.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {items.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    Use the search above to add items
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // Suggestions (when they've arrived) plus the always-available typed row
  const searchView = query.trim().length >= 2
    ? pantrySearchView(query.trim(), searchStatus === 'done' ? searchResults : [], pantryItems)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 gap-3 bg-background">
        <LoadErrorCard error={loadError} onRetry={fetchPantry} title="We couldn't load your Pantry" testId="pantry-load-error" />
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="pantry-load-error-back">Go back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="pantry-back-btn"
            >
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <Package size={24} className="text-accent" />
              <h1 className="font-heading font-bold text-xl">My Pantry</h1>
            </div>
            <div className="ml-auto">
              <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium">
                {getTotalItems()} items
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Global search: a new item is filed under its Core Food's section */}
        <div className="space-y-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search foods you have..."
              className="w-full h-10 pl-9 pr-4 rounded-lg bg-secondary border-none text-sm focus:ring-2 focus:ring-accent"
              data-testid="pantry-search"
            />
            {searchStatus === 'loading' && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {searchView && searchView.results.length > 0 && (function () {
            const renderResult = (result, idx) => {
              const section = CATEGORIES.find(c => c.id === result.pantry_category);
              return (
                <button
                  key={result.food_id}
                  onClick={() => addToPantry(result)}
                  className="w-full p-2 text-left text-sm hover:bg-secondary/50 rounded-lg flex items-center justify-between transition-colors"
                  data-testid={`pantry-result-${idx}`}
                >
                  <span className="truncate">{result.display_name}</span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">{section ? section.name : 'Other'}</span>
                    <Plus size={16} className="text-accent" />
                  </span>
                </button>
              );
            };
            // First five Core Food choices are the immediate visible area; any
            // additional returned matches live in the existing scroll area.
            const visible = searchView.results.slice(0, 5);
            const overflow = searchView.results.slice(5);
            return (
              <div className="space-y-1">
                {visible.map((result, idx) => renderResult(result, idx))}
                {overflow.length > 0 && (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {overflow.map((result, idx) => renderResult(result, idx + 5))}
                  </div>
                )}
              </div>
            );
          })()}

          {searchView && searchStatus === 'error' && (
            <p className="text-xs text-muted-foreground px-2" data-testid="pantry-search-unavailable">
              Suggestions unavailable right now
            </p>
          )}

          {searchView && searchView.typed.kind === 'already' && (
            <p className="text-xs text-muted-foreground text-center py-2" data-testid="pantry-already-added">
              Already in your Pantry
            </p>
          )}

          {searchView && searchView.typed.kind === 'add' && (
            <button
              onClick={addTypedToPantry}
              className="w-full p-2 text-left text-sm hover:bg-secondary/50 rounded-lg flex items-center justify-between transition-colors"
              data-testid="pantry-add-custom"
            >
              <span className="truncate">{`+ Add "${query.trim()}" to Pantry`}</span>
              <Plus size={16} className="text-accent flex-shrink-0" />
            </button>
          )}

          {pendingAdd && (
            <p className="text-xs text-muted-foreground px-2" data-testid="pantry-adding">
              {`Adding ${pendingAdd}…`}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={findMealsWithPantry}
            disabled={getTotalItems() === 0}
            className="flex-1 rounded-full bg-accent text-white"
            data-testid="find-meals-btn"
          >
            <Sparkles size={16} className="mr-2" />
            Find Meals I Can Make
          </Button>
          {getTotalItems() > 0 && (
            <Button
              onClick={clearPantry}
              variant="outline"
              className="rounded-full"
              data-testid="clear-btn"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Category Sections */}
        {CATEGORIES.map(renderCategory)}
      </div>

      <BottomNav />
    </div>
  );
}

export default Pantry;
